import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectListResponse } from '@app/components/my-organizations-sidebar/project-list-view';
import { CreateNewProjectDto } from '@app/models/dtos/projectDto';
import {
    AddProjectsToMemberRequest,
    DeleteMemberFromProjectsRequest,
    DeleteMemberRequest,
    GetMemberRequest,
    getProjectRequestDto,
    MemberDto,
    MemberInvitationDto,
    paginatedProjectResponse,
    PolicyData,
    PolicyRequest,
    ProjectRequest,
    UpdateProjectRoleOfARequest
} from '@app/store/project/types';
import { updateMemberRoleRequest } from '@app/store/organization/types';
import { RemoveInvitationType, SendInvitationType } from '@app/store/invitation/types';
import { GetSettingsResponse } from '@app/components/organization/settings-tab';
import { customBaseQueryWithReauth } from '../refresh/customBaseQuery';

export const PROJECT_PATH = 'project';
export const PROJECT_TAG = 'PROJECT_TAG';
export const PROJECT_MEMBER_TAG = 'PROJECT_MEMBER';
export const POLICY_TAG = 'POLICY_TAG';
export const PROJECT_CONFIG_TAG = 'PROJECT_CONFIG_TAG';
export const CREATE_PROJECT_TAG = 'CREATE_PROJECT_TAG';
export const ORGANIZATION_MEMBER_PROJECTS_TAG = 'ORGANIZATION_MEMBER_PROJECTS_TAG';

export interface QueryParamsType {
    organizationId: string | Array<string>;
    projectId?: string | Array<string>;
}

export interface LogsType extends QueryParamsType {
    currentDate: string;
}

export interface AuditLogsResponse {
    isLast: boolean;
    data: Array<AuditTrailResponse>;
}

export interface AddMembersToProjectBodyType extends QueryParamsType {
    usersArray: Array<{ userId: string; role: string }>;
}

export interface GetPolicyResponse {
    blacklists: Array<string>;
    whitelists: Array<string>;
    configurations: PolicyData;
}

export interface AuditTrailResponse {
    id: string;
    userId: string;
    userName: string;
    documentId: string;
    organizationName: string;
    projectName: string;
    documentName: string;
    targetUser: string;
    eventType: string;
    eventDate: number;
    newValue: string;
    currentValue: string;
    severity: string;
    meta: any;
}

export const projectApi = createApi({
    reducerPath: PROJECT_PATH,
    tagTypes: [PROJECT_TAG, PROJECT_MEMBER_TAG, POLICY_TAG, PROJECT_CONFIG_TAG, CREATE_PROJECT_TAG, ORGANIZATION_MEMBER_PROJECTS_TAG],
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
    keepUnusedDataFor: 0,
    baseQuery: customBaseQueryWithReauth,
    endpoints: (builder) => ({
        getProjectsInOrganization: builder.query<{ nonDeletedProjects: Array<ProjectListResponse>; allProjects: Array<ProjectListResponse> }, { organizationId?: string; query?: string }>({
            query: ({ organizationId, query }) => ({
                url: `/organizations/${organizationId}/projects/mine`,
                method: 'GET',
                params: { query }
            }),
            transformResponse: (response: { nonDeletedProjects: Array<ProjectListResponse>; allProjects: Array<ProjectListResponse> }) => {
                return {
                    nonDeletedProjects: response.nonDeletedProjects.sort((a, b) => a.projectName.localeCompare(b.projectName)),
                    allProjects: response.allProjects.sort((a, b) => a.projectName.localeCompare(b.projectName))
                };
            },
            providesTags: [PROJECT_TAG, CREATE_PROJECT_TAG]
        }),

        getProjectsWhereOrganizationMemberNotInvolved: builder.query<Array<ProjectListResponse>, any>({
            query: ({ organizationId, userId, query }) => ({
                url: `/organizations/${organizationId}/user/${userId}/not-involved-projects`,
                method: 'GET',
                params: {
                    query: query
                }
            }),
            transformResponse: (response: Array<ProjectListResponse>) => {
                // Sort the response alphabetically
                return response.sort((a, b) => a.projectName.localeCompare(b.projectName));
            },
            providesTags: [PROJECT_TAG]
        }),
        updateProjectDetails: builder.mutation<any, ProjectRequest>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/projects/${request.projectId}`,
                method: 'PATCH',
                body: request.requestBody
            }),
            invalidatesTags: [PROJECT_TAG]
        }),
        getProjectDetails: builder.query<ProjectListResponse, QueryParamsType>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/projects/${request.projectId}`,
                method: 'GET'
            }),
            providesTags: [PROJECT_TAG]
        }),
        deleteProject: builder.mutation<void, any>({
            query: ({ organizationId, projectId }) => ({
                url: `organizations/${organizationId}/projects/${projectId}`,
                method: 'DELETE'
            }),
            invalidatesTags: [PROJECT_TAG]
        }),
        getProjectMembers: builder.query<Array<MemberDto | MemberInvitationDto>, any>({
            query: (request: GetMemberRequest) => ({
                url: `/organizations/${request.organizationId}/projects/${request.projectId}/members`,
                method: 'GET',
                params: {
                    query: request.query,
                    sort_by: request.sort_by
                }
            }),
            providesTags: [PROJECT_MEMBER_TAG]
        }),
        deleteProjectMembers: builder.mutation<void, DeleteMemberRequest>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/projects/${request.projectId}/members`,
                method: 'DELETE',
                body: request.userIds
            }),
            invalidatesTags: [PROJECT_MEMBER_TAG]
        }),
        addNewProjectToOrganization: builder.mutation<{ projectId: string }, CreateNewProjectDto>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects`,
                method: 'POST',
                body: {
                    projectName: requestBody.name,
                    projectDescription: requestBody.description,
                    projectPolicyType: requestBody.policyType
                }
            }),
            invalidatesTags: [CREATE_PROJECT_TAG]
        }),
        getOrganizationMembersNotInTheProject: builder.query<any, QueryParamsType>({
            query: (requestParams) => ({
                url: `/organizations/${requestParams.organizationId}/projects/${requestParams.projectId}/members-not-in-project`,
                method: 'GET'
            }),
            providesTags: [PROJECT_MEMBER_TAG]
        }),
        addMembersToTheProject: builder.mutation<any, AddMembersToProjectBodyType>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/members`,
                method: 'POST',
                body: requestBody.usersArray
            }),
            invalidatesTags: [PROJECT_MEMBER_TAG]
        }),

        addProjectsToMember: builder.mutation<any, AddProjectsToMemberRequest>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/members/${requestBody.userId}/add-member-to-projects`,
                method: 'POST',
                body: requestBody.projects
            }),
            invalidatesTags: [PROJECT_MEMBER_TAG, ORGANIZATION_MEMBER_PROJECTS_TAG]
        }),
        sendInvitationToMemberFromProject: builder.mutation<MemberInvitationDto, SendInvitationType>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/invitation/send`,
                method: 'POST',
                body: {
                    email: requestBody.email,
                    role: requestBody.role
                }
            }),
            invalidatesTags: [PROJECT_MEMBER_TAG]
        }),
        removeProjectInvitation: builder.mutation<any, RemoveInvitationType>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/invitations/${requestBody.invitationToken}`,
                method: 'DELETE'
            }),
            invalidatesTags: [PROJECT_MEMBER_TAG]
        }),
        updateProjectInvitationRole: builder.mutation<any, updateMemberRoleRequest>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/invitation/${requestBody.identifier}`,
                method: 'PATCH',
                params: {
                    role: requestBody.role
                }
            }),
            invalidatesTags: [PROJECT_MEMBER_TAG]
        }),
        updateProjectMemberRole: builder.mutation<any, updateMemberRoleRequest>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/members/${requestBody.identifier}`,
                method: 'PATCH',
                params: {
                    role: requestBody.role
                }
            }),
            invalidatesTags: [PROJECT_MEMBER_TAG]
        }),

        updateProjectRoleOfMember: builder.mutation<any, UpdateProjectRoleOfARequest>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/members/${requestBody.userId}/update-project-roles`,
                method: 'PATCH',
                body: requestBody.projects
            }),
            invalidatesTags: [ORGANIZATION_MEMBER_PROJECTS_TAG]
        }),

        deleteMemberFromProjects: builder.mutation<void, DeleteMemberFromProjectsRequest>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/members/${request.userId}/remove-from-projects`,
                method: 'DELETE',
                body: request.projectIds
            }),
            invalidatesTags: [ORGANIZATION_MEMBER_PROJECTS_TAG]
        }),
        getProjectAuditLogs: builder.query<AuditLogsResponse, LogsType>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/logs?currentDate=${requestBody.currentDate}`,
                method: 'GET'
            })
        }),
        getProjectLevelConfigs: builder.query<Array<GetSettingsResponse>, { organizationId: string; projectId: string }>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/configs`,
                method: 'GET'
            }),
            providesTags: [PROJECT_CONFIG_TAG],
            transformResponse: (response: Array<GetSettingsResponse>) => {
                return response.sort((a, b) => {
                    const keyA = a.settingsName.toLowerCase();
                    const keyB = b.settingsName.toLowerCase();
                    if (keyA < keyB) return -1;
                    if (keyA > keyB) return 1;
                    return 0;
                });
            }
        }),

        getPolicy: builder.query<PolicyData, { organizationId: string; projectId: string }>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/policy`,
                method: 'GET',
                cache: 'no-cache'
            }),
            providesTags: [POLICY_TAG]
        }),

        putPolicy: builder.mutation<any, PolicyRequest>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/policy`,
                method: 'PUT',
                body: requestBody.policyData
            }),
            invalidatesTags: [POLICY_TAG]
        }),

        updateProjectLevelConfigs: builder.mutation<
            any,
            {
                organizationId: string;
                projectId: string;
                settingsRequestBodyList: Array<{ settingsId: string; settingsValue: string }>;
            }
        >({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/configs`,
                method: 'PATCH',
                body: requestBody.settingsRequestBodyList
            }),
            invalidatesTags: [PROJECT_CONFIG_TAG, PROJECT_TAG]
        }),

        getProjectsOfOrganizationMember: builder.query<paginatedProjectResponse, getProjectRequestDto>({
            query: ({ organizationId, userId, requestParams }) => ({
                url: `/organizations/${organizationId}/user/${userId}/projects`,
                method: 'POST',
                params: {
                    query: requestParams?.query,
                    page: requestParams?.page,
                    size: requestParams?.size
                }
            }),
            providesTags: [ORGANIZATION_MEMBER_PROJECTS_TAG]
        })
    })
});

export const {
    useGetProjectsInOrganizationQuery,
    useRemoveProjectInvitationMutation,
    useLazyGetProjectsInOrganizationQuery,
    useAddNewProjectToOrganizationMutation,
    useLazyGetOrganizationMembersNotInTheProjectQuery,
    useGetOrganizationMembersNotInTheProjectQuery,
    useUpdateProjectDetailsMutation,
    useDeleteProjectMutation,
    useDeleteProjectMembersMutation,
    useGetProjectMembersQuery,
    useAddMembersToTheProjectMutation,
    useSendInvitationToMemberFromProjectMutation,
    useGetProjectLevelConfigsQuery,
    useUpdateProjectLevelConfigsMutation,
    useUpdateProjectMemberRoleMutation,
    useLazyGetProjectAuditLogsQuery,
    useGetProjectDetailsQuery,
    useUpdateProjectInvitationRoleMutation,
    useGetPolicyQuery,
    usePutPolicyMutation,
    useGetProjectsOfOrganizationMemberQuery,
    useGetProjectsWhereOrganizationMemberNotInvolvedQuery,
    useUpdateProjectRoleOfMemberMutation,
    useDeleteMemberFromProjectsMutation,
    useAddProjectsToMemberMutation
} = projectApi;
